// src/features/excursions/ExcursionsTab.jsx
import React, { useState, useMemo } from "react";
import { useExcursionsData } from "./hooks/useExcursionsData";
import LoadingSpinner from "components/LoadingSpinner/LoadingSpinner";
import ExcursionTable from "./components/ExcursionTable";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { Label } from "components/ui/label";
import { Input } from "components/ui/input";
import AddExcursionModal from "./components/AddExcursionModal";
import EditExcursionModal from "./components/EditExcursionModal";
import { Button } from "components/ui/button";
import { CalendarCheck } from "lucide-react";
import { checkHoliday } from "services/holidayService";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    value || 0,
  );

function ExcursionsTab({ userRole, searchTerm }) {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingExcursion, setEditingExcursion] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const {
    excursions,
    totalClients,
    totalPayments,
    isLoading,
    error,
    markAsPresent,
    updateLunchInfo,
    addExcursion,
    updateExcursion,
    deleteExcursion,
  } = useExcursionsData(selectedDate, searchTerm);

  const holidayName = useMemo(() => {
    return checkHoliday(selectedDate);
  }, [selectedDate]);

  const sortedExcursions = useMemo(() => {
    return [...excursions].sort((a, b) => {
      if (sortDirection === "asc") {
        return a.clientName.localeCompare(b.clientName);
      } else {
        return b.clientName.localeCompare(a.clientName);
      }
    });
  }, [excursions, sortDirection]);

  const handleDateChange = (event) => {
    const [year, month, day] = event.target.value.split("-").map(Number);
    setSelectedDate(new Date(year, month - 1, day));
  };

  const handleSort = () => {
    setSortDirection((currentDirection) =>
      currentDirection === "asc" ? "desc" : "asc",
    );
  };

  const canEdit = userRole === "admin" || userRole === "editor";
  const isSearchMode = searchTerm && searchTerm.length > 3;

  return (
    <div className="space-y-6">
      {/* --- VISUALIZAÇÃO DESKTOP --- */}
      <div className="hidden md:flex flex-wrap justify-between items-end gap-4">
        <div className="flex items-end gap-4">
          <div className="w-auto">
            {isSearchMode ? (
              <div className="p-2 bg-blue-100 text-blue-800 rounded border border-blue-200">
                <strong>Modo de Busca:</strong> Exibindo resultados para '
                {searchTerm}'
              </div>
            ) : (
              <>
                <Label
                  htmlFor="excursion-date-desktop"
                  className="font-semibold"
                >
                  Selecione a Data
                </Label>

                {/* ✅ O SEGREDO ESTÁ AQUI: w-fit */}
                {/* w-fit: O container terá o tamanho do seu filho mais largo. */}
                <div className="flex flex-col w-fit">
                  <Input
                    type="date"
                    id="excursion-date-desktop"
                    value={formatDateToYYYYMMDD(selectedDate)}
                    onChange={handleDateChange}
                    // w-full: O input preenche o container. Se o feriado alargar o container, o input cresce junto.
                    // min-w-[...]: Garante um tamanho mínimo decente se não houver feriado ou se o nome for curto.
                    className="mt-2 force-light-datepicker w-full min-w-[160px]"
                  />
                  {holidayName && (
                    // whitespace-nowrap: Força o container a crescer se o texto for longo, em vez de quebrar linha.
                    <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                      <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                      {holidayName}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {!isLoading && !error && !isSearchMode && (
            <>
              <div className="border-l pl-4">
                <p className="text-sm text-muted-foreground">
                  Total de Clientes
                </p>
                <p className="text-2xl font-bold">{totalClients}</p>
              </div>
              <div className="border-l pl-4">
                <p className="text-sm text-muted-foreground">Total Pago</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(totalPayments)}
                </p>
              </div>
            </>
          )}
        </div>
        {canEdit && !isSearchMode && (
          <Button onClick={() => setIsAddModalOpen(true)}>
            Adicionar Excursão
          </Button>
        )}
      </div>

      {/* --- VISUALIZAÇÃO MOBILE --- */}
      <div className="md:hidden space-y-4">
        <div className="flex justify-between items-end gap-4">
          <div className="w-auto">
            {isSearchMode ? (
              <div className="p-2 bg-blue-100 text-blue-800 rounded border border-blue-200 text-xs">
                Busca: '{searchTerm}'
              </div>
            ) : (
              <>
                <Label
                  htmlFor="excursion-date-mobile"
                  className="font-semibold"
                >
                  Selecione a Data
                </Label>

                <div className="flex flex-col w-fit">
                  <Input
                    type="date"
                    id="excursion-date-mobile"
                    value={formatDateToYYYYMMDD(selectedDate)}
                    onChange={handleDateChange}
                    className="mt-2 force-light-datepicker w-full min-w-[160px]"
                  />
                  {holidayName && (
                    <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                      <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                      {holidayName}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {!isLoading && !error && !isSearchMode && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total de Clientes</p>
              <p className="text-2xl font-bold">{totalClients}</p>
            </div>
          )}
        </div>
        <div className="flex justify-between items-end gap-4">
          {canEdit && !isSearchMode && (
            <Button onClick={() => setIsAddModalOpen(true)}>
              Adicionar Excursão
            </Button>
          )}
          {!isLoading && !error && !isSearchMode && (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Pago</p>
              <p className="text-2xl font-bold">
                {formatCurrency(totalPayments)}
              </p>
            </div>
          )}
        </div>
      </div>

      {isLoading && <LoadingSpinner />}
      {error && (
        <p className="text-red-500 bg-red-50 p-4 rounded-md">Erro: {error}</p>
      )}

      {!isLoading && !error && (
        <ExcursionTable
          excursions={sortedExcursions}
          onMarkPresent={markAsPresent}
          onUpdateLunchInfo={updateLunchInfo}
          onUpdateExcursion={updateExcursion}
          canEdit={canEdit}
          onEditExcursion={setEditingExcursion}
          onDeleteExcursion={deleteExcursion}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      <AddExcursionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddExcursion={addExcursion}
        defaultDate={selectedDate}
      />

      {editingExcursion && (
        <EditExcursionModal
          isOpen={!!editingExcursion}
          onClose={() => setEditingExcursion(null)}
          onUpdateExcursion={updateExcursion}
          excursionData={editingExcursion}
        />
      )}
    </div>
  );
}

export default ExcursionsTab;
